// Stremio Plus: core worker entry.
// Wraps @stremio/stremio-core-web's worker and reroutes the requests of secondary
// profiles (library sync and, when the profile has its own addons, the addon collection)
// from api.strem.io to the stremio-plus edge function, adding the profile id.

const STREMIO_API = 'https://api.strem.io/api/';
const LIBRARY_METHODS = ['datastoreMeta', 'datastoreGet', 'datastorePut'];
const ADDON_METHODS = ['addonCollectionGet', 'addonCollectionSet'];

// Addon resources: {base}/{resource}/{type}/{id}(/{extra}).json
const ADDON_RESOURCE = /\/(catalog|meta|stream)\/([^/]+)\/([^/]+?)(?:\/[^/]*)?\.json$/;
const MAX_RATING_BATCH = 200;

let profile = null;
let plusApiUrl = null;
let authKey = null;

const nativeFetch = self.fetch.bind(self);

// ---------- Kids filtering ----------

// content id -> minimum age (null = unknown)
const ratings = new Map();

const baseContentId = (id) => {
    return id.startsWith('tmdb:') ? id.split(':').slice(0, 2).join(':') : id.split(':')[0];
};

const metaGenres = (meta) => {
    if (Array.isArray(meta.genres)) return meta.genres;
    return (meta.links ?? []).filter((link) => link.category === 'Genres').map((link) => link.name);
};

const loadRatings = async (items) => {
    const missing = items.filter(({ id }) => !ratings.has(baseContentId(id)));
    for (let i = 0; i < missing.length; i += MAX_RATING_BATCH) {
        const batch = missing.slice(i, i + MAX_RATING_BATCH);
        try {
            const response = await nativeFetch(`${plusApiUrl}/contentRatings`, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ authKey, items: batch }),
            });
            const json = await response.json();
            if (json.error) throw new Error(json.error.message);
            Object.entries(json.result).forEach(([id, minAge]) => ratings.set(id, minAge));
        } catch (error) {
            // Unknown titles stay hidden; they are retried on the next request.
            console.error('Stremio Plus ratings:', error);
        }
    }
};

const isAllowed = (id) => {
    const minAge = ratings.get(baseContentId(id));
    return typeof minAge === 'number' && minAge <= profile.maxAge;
};

const jsonResponse = (body) => new Response(JSON.stringify(body), {
    status: 200,
    headers: { 'content-type': 'application/json' },
});

const filterAddonResponse = async (resource, type, id, response) => {
    if (!response.ok) return response;
    const json = await response.clone().json().catch(() => null);
    if (json === null) return response;

    if (resource === 'catalog') {
        const metas = Array.isArray(json.metas) ? json.metas.filter((meta) => typeof meta?.id === 'string') : [];
        await loadRatings(metas.map((meta) => ({ id: meta.id, type: meta.type, genres: metaGenres(meta) })));
        return jsonResponse({ ...json, metas: metas.filter((meta) => isAllowed(meta.id)) });
    }

    await loadRatings([{ id, type, genres: json.meta ? metaGenres(json.meta) : undefined }]);
    if (isAllowed(id)) return response;
    return resource === 'meta' ? jsonResponse({ meta: null }) : jsonResponse({ streams: [] });
};

const isKidsProfile = () => profile !== null && profile.isKids === true && plusApiUrl !== null;

const shouldReroute = (method) => {
    return profile !== null && !profile.isMain && (
        LIBRARY_METHODS.includes(method) ||
        (profile.ownAddons && ADDON_METHODS.includes(method))
    );
};

self.fetch = async (input, init) => {
    const request = new Request(input, init);

    if (isKidsProfile() && !request.url.startsWith(STREMIO_API) && !request.url.startsWith(plusApiUrl)) {
        const match = new URL(request.url).pathname.match(ADDON_RESOURCE);
        if (match) {
            const [, resource, type, id] = match;
            return filterAddonResponse(resource, decodeURIComponent(type), decodeURIComponent(id), await nativeFetch(request));
        }
    }

    if (!request.url.startsWith(STREMIO_API)) return nativeFetch(request);

    const method = request.url.slice(STREMIO_API.length).split('?')[0];
    if (!shouldReroute(method)) return nativeFetch(request);

    const body = await request.clone().json();
    const response = await nativeFetch(`${plusApiUrl}/${method}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...body, profileId: profile.id }),
    });

    // A profile whose own addons were never saved falls back to the account addons.
    if (method === 'addonCollectionGet') {
        const json = await response.clone().json().catch(() => null);
        if (json?.result?.addons === null) return nativeFetch(request);
    }

    return response;
};

require('@stremio/stremio-core-web/worker.js');

const init = self.init;
self.init = (args) => {
    profile = args.stremioPlus?.profile ?? null;
    plusApiUrl = args.stremioPlus?.apiUrl ?? null;
    authKey = args.stremioPlus?.authKey ?? null;
    return init(args);
};
